import { useMemo, useState } from 'react';
import { Loader2, Plus, FileText, Search, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useBoletos } from './useBoletos';
import { BoletoModal } from './BoletoModal';
import { MarkAsPaidDialog } from './MarkAsPaidDialog';
import { BoletoRow } from './BoletoRow';
import { BOLETO_STATUS_LABELS, DEFAULT_BOLETO_FILTERS, effectiveStatus } from './types';
import type { BillingInvoice, BoletoFilters, BoletoStatus } from './types';

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function BoletosPage() {
  const { clientId, client } = useCurrentClient();
  const [filters, setFilters] = useState<BoletoFilters>(DEFAULT_BOLETO_FILTERS);
  const { data: boletos = [], isLoading } = useBoletos(filters);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<BillingInvoice | null>(null);
  const [paying, setPaying] = useState<BillingInvoice | null>(null);

  const summary = useMemo(() => {
    let pendingCount = 0;
    let pendingAmount = 0;
    let overdueCount = 0;
    let overdueAmount = 0;
    let paidCount = 0;
    let paidAmount = 0;
    for (const b of boletos) {
      const eff = effectiveStatus(b);
      if (eff === 'pending') {
        pendingCount += 1;
        pendingAmount += Number(b.amount);
      } else if (eff === 'overdue') {
        overdueCount += 1;
        overdueAmount += Number(b.amount);
      } else if (eff === 'paid') {
        paidCount += 1;
        paidAmount += Number(b.paid_amount ?? b.amount);
      }
    }
    return {
      pendingCount,
      pendingAmount,
      overdueCount,
      overdueAmount,
      paidCount,
      paidAmount,
    };
  }, [boletos]);

  const hasActiveFilters =
    filters.search.trim() !== '' ||
    filters.status !== 'all' ||
    filters.fromDate !== '' ||
    filters.toDate !== '';

  if (!clientId) {
    return (
      <div>
        <h1 className="mb-1 font-brand text-2xl font-bold text-foreground">Boletos</h1>
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <FileText className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">Selecione um cliente no topo para gerenciar boletos.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Boletos</h1>
          <p className="text-sm text-muted-foreground">
            {client?.name ? `${client.name} · ` : ''}cobranças e recebimentos
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-primary font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} className="mr-2" />
          Novo boleto
        </Button>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        <AlertTriangle size={14} className="mr-1.5 inline" />
        <strong>Modo manual.</strong> Cadastro local de boletos. A integração com Asaas (geração +
        atualização automática de status via webhook) ainda não está ativa.
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              A receber (pendentes)
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-amber-600">
              {formatBrl(summary.pendingAmount)}
            </p>
            <p className="text-xs text-muted-foreground">
              {summary.pendingCount} boleto{summary.pendingCount === 1 ? '' : 's'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Vencidos
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-expense">
              {formatBrl(summary.overdueAmount)}
            </p>
            <p className="text-xs text-muted-foreground">
              {summary.overdueCount} boleto{summary.overdueCount === 1 ? '' : 's'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Recebido (pagos)
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-income">
              {formatBrl(summary.paidAmount)}
            </p>
            <p className="text-xs text-muted-foreground">
              {summary.paidCount} boleto{summary.paidCount === 1 ? '' : 's'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Buscar</Label>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  placeholder="Pagador, descrição, CPF/CNPJ…"
                  value={filters.search}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select
                value={filters.status}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, status: v as BoletoStatus | 'all' }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="pending">{BOLETO_STATUS_LABELS.pending}</SelectItem>
                  <SelectItem value="paid">{BOLETO_STATUS_LABELS.paid}</SelectItem>
                  <SelectItem value="overdue">{BOLETO_STATUS_LABELS.overdue}</SelectItem>
                  <SelectItem value="canceled">{BOLETO_STATUS_LABELS.canceled}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Vence de</Label>
                <Input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Até</Label>
                <Input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
                />
              </div>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end border-t border-border pt-3">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setFilters(DEFAULT_BOLETO_FILTERS)}
                className="h-7 text-xs"
              >
                <X size={12} className="mr-1" />
                Limpar filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabela */}
      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : boletos.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <FileText className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">
              {hasActiveFilters
                ? 'Nenhum boleto corresponde aos filtros.'
                : 'Nenhum boleto cadastrado.'}
            </p>
            {!hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setCreateOpen(true)}
              >
                <Plus size={14} className="mr-1.5" />
                Criar primeiro boleto
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/40 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 text-left">Pagador</th>
                    <th className="px-3 py-2 text-left">Descrição</th>
                    <th className="px-3 py-2 text-right">Valor</th>
                    <th className="px-3 py-2 text-left">Vencimento</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {boletos.map((b) => (
                    <BoletoRow
                      key={b.id}
                      boleto={b}
                      onEdit={() => setEditing(b)}
                      onMarkPaid={() => setPaying(b)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <BoletoModal open={createOpen} onOpenChange={setCreateOpen} />
      <BoletoModal
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        edit={editing ?? undefined}
      />
      <MarkAsPaidDialog
        open={!!paying}
        onOpenChange={(v) => !v && setPaying(null)}
        boleto={paying}
      />
    </div>
  );
}
